import { useEffect, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  BarChart3, Plus, Search, X, Trash2, CheckCircle2, XCircle, Clock, CalendarClock,
  User, Vote, Lock, Unlock, ChevronRight,
} from "lucide-react";
import { V } from "@/lib/vocab";

const PIE = ["#2563eb", "#dc2626", "#16a34a", "#d97706", "#7c3aed", "#0891b2", "#db2777", "#65a30d"];
const fmtDate = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };
const STATUS = {
  open: { label: "OPEN", cls: "text-emerald-700" },
  scheduled: { label: "SCHEDULED", cls: "text-amber-700" },
  closed: { label: "CLOSED", cls: "text-ink-400" },
};

function Pie({ results, total }) {
  if (!total) return <div className="h-40 w-40 rounded-full bg-sand-100 flex items-center justify-center text-xs text-muted-foreground mx-auto">No votes yet</div>;
  let acc = 0;
  const R = 70, C = 80;
  const arcs = results.filter((r) => r.votes > 0).map((r, i) => {
    const frac = r.votes / total;
    const a0 = acc * 2 * Math.PI - Math.PI / 2; acc += frac;
    const a1 = acc * 2 * Math.PI - Math.PI / 2;
    const large = frac > 0.5 ? 1 : 0;
    const x0 = C + R * Math.cos(a0), y0 = C + R * Math.sin(a0);
    const x1 = C + R * Math.cos(a1), y1 = C + R * Math.sin(a1);
    if (frac >= 1) return <circle key={i} cx={C} cy={C} r={R} fill={PIE[i % PIE.length]} />;
    return <path key={i} d={`M${C},${C} L${x0},${y0} A${R},${R} 0 ${large} 1 ${x1},${y1} Z`} fill={PIE[i % PIE.length]} />;
  });
  return <svg viewBox="0 0 160 160" className="h-40 w-40 mx-auto">{arcs}</svg>;
}

const emptySurvey = () => ({ title: "", description: "", from_date: "", to_date: "", options: [{ text: "" }, { text: "" }], allow_delinquent: true, results_public: false, notify_residents: true });

export default function Surveys() {
  const { tenantId } = useDash();
  const [surveys, setSurveys] = useCachedState(`surveys:${tenantId}`, []);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptySurvey());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null); // full survey with results
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    if (!tenantId) return;
    const params = {};
    if (q) params.q = q;
    if (statusFilter) params.status = statusFilter;
    api.get(`/tenants/${tenantId}/surveys`, { params }).then((r) => setSurveys(r.data || [])).catch(() => {});
  }, [tenantId, q, statusFilter]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  const openDetail = async (s) => {
    try { const r = await api.get(`/tenants/${tenantId}/surveys/${s.id}`); setDetail(r.data); }
    catch { toast.error("Could not open survey"); }
  };

  const addOption = () => setForm((f) => ({ ...f, options: [...f.options, { text: "" }] }));
  const setOption = (i, v) => setForm((f) => ({ ...f, options: f.options.map((o, j) => j === i ? { text: v } : o) }));
  const rmOption = (i) => setForm((f) => ({ ...f, options: f.options.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Add a title / question"); return; }
    if (form.options.filter((o) => o.text.trim()).length < 2) { toast.error("Add at least two options"); return; }
    setSaving(true);
    try {
      await api.post(`/tenants/${tenantId}/surveys`, form);
      toast.success("Survey created"); setOpen(false); setForm(emptySurvey()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
    finally { setSaving(false); }
  };

  const endVoting = () => setConfirm({
    title: "End this survey?",
    body: `${V.many("person")} will no longer be able to vote. You can reopen it later if needed.`,
    confirmLabel: "End voting", danger: false,
    checkbox: { label: `Notify ${V.lcs("person")} of the final results (text & email, opted-in only)`, value: true },
    onConfirm: async (checked) => {
      try {
        const r = await api.post(`/tenants/${tenantId}/surveys/${detail.id}/close`, { notify: !!checked });
        setDetail(r.data); load();
        const st = r.data.notified;
        toast.success(st ? `Voting ended · results sent to ${st.sms_sent} phone${st.sms_sent === 1 ? "" : "s"} & ${st.email_sent} email${st.email_sent === 1 ? "" : "s"}` : "Voting ended");
      }
      catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    },
  });
  const reopenVoting = async () => {
    try { const r = await api.post(`/tenants/${tenantId}/surveys/${detail.id}/reopen`); setDetail(r.data); load(); toast.success("Voting reopened"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const del = () => setConfirm({
    title: "Delete this survey?", body: "This removes the survey and all its votes. This can't be undone.",
    onConfirm: async () => { await api.delete(`/tenants/${tenantId}/surveys/${detail.id}`); toast.success("Survey deleted"); setDetail(null); load(); },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <PageTitle title={<><BarChart3 className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Surveys</>} about={<>Create a poll to include & engage {V.lcs("people")} in {V.lc("org")} decisions.</>} />
        </div>
        <button data-testid="create-survey-btn" onClick={() => { setForm(emptySurvey()); setOpen(true); }} className="rounded-full bg-redline-500 text-white font-semibold px-4 py-2.5 flex items-center gap-2 hover:bg-redline-600 transition-colors"><Plus className="h-4 w-4" strokeWidth={2.5} /> Create survey</button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-sand-400" />
          <input data-testid="survey-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keyword or creator"
            className="w-full rounded-full border border-sand-200 bg-white pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
        </div>
        <select data-testid="survey-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-full border border-sand-200 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30">
          <option value="">All statuses</option><option value="open">Open</option><option value="scheduled">Scheduled</option><option value="closed">Closed</option>
        </select>
        <span className="text-sm text-muted-foreground ml-auto">Total {surveys.length}</span>
      </div>

      <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden" data-testid="surveys-list">
        <div className="hidden sm:grid grid-cols-[2.4fr_1.4fr_1.2fr_auto] gap-4 px-6 py-3 border-b border-sand-200 text-xs uppercase tracking-wider font-bold text-muted-foreground">
          <div>Title</div><div>Created by</div><div>Voting frame</div><div className="text-right">Votes</div>
        </div>
        {surveys.length === 0 && <div className="p-12 text-center text-muted-foreground flex flex-col items-center gap-2"><Vote className="h-8 w-8 text-sand-300" /> No surveys yet. Create one to poll your {V.lcs("people")}.</div>}
        {surveys.map((s) => (
          <button key={s.id} data-testid={`survey-${s.id}`} onClick={() => openDetail(s)} className="w-full text-left flex flex-col gap-2 sm:grid sm:grid-cols-[2.4fr_1.4fr_1.2fr_auto] sm:gap-4 sm:items-center px-6 py-4 border-b border-sand-100 last:border-0 hover:bg-sand-50/60 transition-colors">
            <div className="min-w-0">
              <div className={`text-[10px] font-bold tracking-wide ${STATUS[s.status]?.cls}`}>{STATUS[s.status]?.label}</div>
              <div className="font-semibold text-redline-600 line-clamp-2">{s.title}</div>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="h-8 w-8 rounded-full bg-sand-100 flex items-center justify-center shrink-0"><User className="h-4 w-4 text-ink-400" /></span>
              <span className="text-sm text-ink-600 truncate">{s.created_by_name || "—"}</span>
            </div>
            <div className="text-sm text-ink-600">{s.from_date ? fmtDate(s.from_date) : "—"} <span className="text-ink-400">until</span> {s.to_date ? fmtDate(s.to_date) : "—"}</div>
            <div className="flex items-center gap-1.5 sm:justify-end text-sm font-semibold text-ink"><span>{s.total_votes}</span><ChevronRight className="h-4 w-4 text-sand-400" /></div>
          </button>
        ))}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Create a survey</DialogTitle><DialogDescription>Ask {V.lcs("people")} a question and give them options to vote on.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-semibold">Question / title</label><textarea data-testid="survey-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} rows={2} placeholder="Do you agree with…?" className="mt-1 w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 resize-y" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-semibold">Voting from</label><input data-testid="survey-from" type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" /></div>
              <div><label className="text-sm font-semibold">Voting until</label><input data-testid="survey-to" type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" /></div>
            </div>
            <div>
              <label className="text-sm font-semibold">Options</label>
              <div className="mt-1.5 space-y-2">
                {form.options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="h-8 w-8 rounded-lg bg-sand-100 flex items-center justify-center font-bold text-sm text-ink-500 shrink-0">{String.fromCharCode(65 + i)}</span>
                    <input data-testid={`survey-option-${i}`} value={o.text} onChange={(e) => setOption(i, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + i)}`} className="flex-1 rounded-xl border border-sand-200 px-3.5 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                    {form.options.length > 2 && <button onClick={() => rmOption(i)} className="h-8 w-8 rounded-lg hover:bg-red-50 text-ink-400 hover:text-redline-600 flex items-center justify-center"><X className="h-4 w-4" /></button>}
                  </div>
                ))}
                <button type="button" data-testid="survey-add-option" onClick={addOption} className="text-sm font-semibold text-redline-600 hover:text-redline-700 inline-flex items-center gap-1"><Plus className="h-4 w-4" /> Add option</button>
              </div>
            </div>
            <label className="flex items-start gap-2.5 rounded-xl border border-sand-200 bg-sand-50 p-3 cursor-pointer">
              <input type="checkbox" data-testid="survey-notify" checked={form.notify_residents} onChange={(e) => setForm({ ...form, notify_residents: e.target.checked })} className="mt-0.5 h-4 w-4 accent-redline-500" />
              <span className="text-xs text-ink-700"><span className="font-semibold text-ink block">Text {V.lcs("person")} when this survey opens</span>Sends a one-time "new survey — reply to vote" text to consented {V.lcs("person")} the moment it's open (or on the start date if scheduled). Turn off to launch silently.</span>
            </label>
            <label className="flex items-start gap-2.5 rounded-xl border border-sand-200 bg-sand-50 p-3 cursor-pointer">
              <input type="checkbox" data-testid="survey-results-public" checked={form.results_public} onChange={(e) => setForm({ ...form, results_public: e.target.checked })} className="mt-0.5 h-4 w-4 accent-redline-500" />
              <span className="text-xs text-ink-700"><span className="font-semibold text-ink block">Show live results to {V.lcs("person")}</span>When on, {V.lcs("person")} get the current tally in their vote confirmation text. Off keeps results private to the team.</span>
            </label>
            <label className="flex items-start gap-2.5 rounded-xl border border-sand-200 bg-sand-50 p-3 cursor-pointer">
              <input type="checkbox" data-testid="survey-delinquent" checked={form.allow_delinquent} onChange={(e) => setForm({ ...form, allow_delinquent: e.target.checked })} className="mt-0.5 h-4 w-4 accent-redline-500" />
              <span className="text-xs text-ink-700"><span className="font-semibold text-ink block">Accept votes from delinquent {V.lcs("person")}</span>Allow {V.lcs("person")} behind on payments to vote (applies once billing is connected).</span>
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-full px-5 py-2.5 font-semibold text-ink-700 hover:bg-sand-100 transition-colors">Cancel</button>
            <button data-testid="survey-save-btn" disabled={saving} onClick={save} className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-60">{saving ? "Creating…" : "Create survey"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / results */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="survey-detail">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-redline-600 text-xl leading-snug pr-6">{detail.title}</DialogTitle>
                <DialogDescription className="font-semibold text-ink-500">{detail.from_date ? fmtDate(detail.from_date) : "—"} until {detail.to_date ? fmtDate(detail.to_date) : "—"}</DialogDescription>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Options residents vote on (read-only preview — residents vote by text) */}
                <div>
                  {detail.description && <p className="text-sm text-ink-600 mb-3">{detail.description}</p>}
                  <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Options {V.lcs("person")} choose from</div>
                  <div className="space-y-2">
                    {detail.results.map((o, i) => (
                      <div key={o.key} data-testid={`option-${o.key}`} className="flex items-center gap-3 rounded-xl border border-sand-200 px-4 py-3">
                        <span className="h-7 w-7 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 text-white" style={{ background: PIE[i % PIE.length] }}>{o.key}</span>
                        <span className="text-sm font-semibold text-ink flex-1">{o.text}</span>
                        <span className="text-xs text-ink-400">{o.votes} vote{o.votes === 1 ? "" : "s"}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 rounded-xl bg-sand-50 border border-sand-200 px-4 py-3 text-xs text-ink-600 flex items-start gap-2">
                    <Vote className="h-4 w-4 text-redline-500 shrink-0 mt-0.5" />
                    {detail.status === "open"
                      ? `Voting is open — ${V.lcs("person")} cast their vote by text. Live results appear on the right.`
                      : detail.status === "scheduled"
                      ? `Voting opens ${detail.from_date ? fmtDate(detail.from_date) : "on the start date"}.`
                      : "Voting has ended. Results are final."}
                  </div>
                </div>
                {/* Results */}
                <div className="rounded-2xl border border-sand-200 p-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Results · {detail.total_votes} votes</div>
                  <Pie results={detail.results} total={detail.total_votes} />
                  <div className="mt-4 space-y-2">
                    {detail.results.map((o, i) => (
                      <div key={o.key} data-testid={`result-${o.key}`} className="flex items-center gap-3">
                        <span className="h-3 w-3 rounded-full shrink-0" style={{ background: PIE[i % PIE.length] }} />
                        <span className="text-sm font-semibold text-ink w-6">{o.key}</span>
                        <span className="text-sm text-ink-600 flex-1">{o.votes} Votes</span>
                        <span className="text-sm font-bold text-redline-600">{o.pct}%</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-sand-100 text-xs text-ink-500 flex items-center gap-2">
                    <User className="h-3.5 w-3.5" /> Created by <span className="font-semibold text-ink">{detail.created_by_name || "—"}</span>
                  </div>
                  <div className="mt-1 text-xs flex items-center gap-2">
                    {detail.status === "closed" ? <span className="text-ink-400 flex items-center gap-1"><XCircle className="h-3.5 w-3.5" /> Closed</span> : detail.status === "open" ? <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Open</span> : <span className="text-amber-700 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Scheduled</span>}
                  </div>
                </div>
              </div>
              <DialogFooter className="border-t border-sand-100 pt-3">
                <button data-testid="survey-delete-btn" onClick={del} className="rounded-full px-4 py-2 text-sm font-semibold text-danger-600 hover:bg-red-50 transition-colors inline-flex items-center gap-1.5 mr-auto"><Trash2 className="h-4 w-4" /> Delete survey</button>
                {detail.status === "closed"
                  ? <button data-testid="survey-reopen" onClick={reopenVoting} className="rounded-full bg-white border border-sand-200 font-semibold px-5 py-2 text-sm hover:border-ink transition-colors inline-flex items-center gap-1.5"><Unlock className="h-4 w-4" /> Reopen voting</button>
                  : detail.status === "open"
                  ? <button data-testid="survey-end" onClick={endVoting} className="rounded-full bg-white border border-amber-300 text-amber-800 font-semibold px-5 py-2 text-sm hover:bg-amber-50 transition-colors inline-flex items-center gap-1.5"><Lock className="h-4 w-4" /> End voting</button>
                  : null}
                <button data-testid="survey-done" onClick={() => setDetail(null)} className="rounded-full bg-ink text-white font-semibold px-6 py-2 text-sm hover:bg-ink-700 transition-colors">Done</button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="survey-confirm">
          <DialogHeader><DialogTitle className="font-heading">{confirm?.title}</DialogTitle><DialogDescription>{confirm?.body}</DialogDescription></DialogHeader>
          {confirm?.checkbox && (
            <label data-testid="survey-notify-checkbox" className="flex items-start gap-2.5 rounded-xl border border-sand-200 bg-sand-50 px-3.5 py-3 cursor-pointer">
              <input type="checkbox" checked={confirm.checkbox.value}
                onChange={(e) => setConfirm({ ...confirm, checkbox: { ...confirm.checkbox, value: e.target.checked } })}
                className="mt-0.5 h-4 w-4 accent-[#e11d48]" />
              <span className="text-sm text-ink-700">{confirm.checkbox.label}</span>
            </label>
          )}
          <DialogFooter>
            <button onClick={() => setConfirm(null)} className="rounded-full px-5 py-2.5 font-semibold text-ink-700 hover:bg-sand-100 transition-colors">Cancel</button>
            <button data-testid="survey-confirm-ok" onClick={async () => { const fn = confirm.onConfirm; const val = confirm.checkbox?.value; setConfirm(null); await fn?.(val); }} className={`rounded-full font-semibold px-6 py-2.5 text-white transition-colors ${confirm?.danger === false ? "bg-ink hover:bg-ink-700" : "bg-danger-500 hover:bg-danger-600"}`}>{confirm?.confirmLabel || "Delete"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
